import { pageTitle } from '@palaccount/config';
import {
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
  type NotificationRow,
} from '@palaccount/contracts';
import {
  Bell,
  BellOff,
  CalendarX2,
  CheckCheck,
  Contact,
  Inbox,
  Landmark,
  Package,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/format';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useMarkAllRead, useNotifications, useOpenNotification } from './api';
import { pathForNotification } from './routes';

/**
 * ADM-04 — NOTIFICATIONS (§15.5).
 *
 * "This screen lets you read the things the system wants to tell you. You use
 * it when the red dot appears on the bell in the top bar and you want to know
 * why."
 *
 * THE PROTOTYPE'S THREE DEAD CONTROLS ARE ALL WIRED HERE, because §18 lists
 * them by name as the thing a build must not copy: "The prototype's rows, the
 * Mark all as read button, and the chip badges are all display-only. A build
 * must wire every one of them."
 *
 *   the ROWS open the record they name and mark themselves read
 *     (BR-ADM-04-06);
 *   MARK ALL AS READ applies to the chosen chip and asks nothing
 *     (BR-ADM-04-07);
 *   the BADGES count unread only and vanish at zero (BR-ADM-04-03).
 *
 * ── WHY EVERY SENTENCE ON THIS SCREEN IS BUILT FROM A DICTIONARY ───────────
 *
 * The API returns `{kind, params}` and never a rendered body. A sentence
 * frozen at raise time would be frozen in the language of whoever happened to
 * trigger it; here the Arabic reader gets an Arabic backlog, and every
 * notification body is under the same EN/AR parity test as the rest of the
 * product (§21.17). BR-ADM-04-13 — "must name the thing, the number, and the
 * party where there is one" — is then a property of a dictionary entry a
 * reviewer can read.
 *
 * ── WHAT IS NOT HERE ───────────────────────────────────────────────────────
 *
 * No per-row menu, no dismiss, no snooze (§6: "There is no per-row menu, no
 * dismiss, no snooze"). No mark-as-unread (§7: "There is deliberately no way
 * back"; the database refuses it too). No delete (BR-ADM-04-10).
 */

/** §5's category table: the icon and the two colours, per category. */
const CATEGORY_STYLE: Record<
  NotificationCategory,
  { icon: LucideIcon; tile: string; pill: string }
> = {
  // "Banks and Checks · account_balance · Brand blue"
  banks: {
    icon: Landmark,
    tile: 'bg-primary/12 text-foreground',
    pill: 'bg-primary/12 text-foreground',
  },
  // "Parties · contacts · Green"
  parties: {
    icon: Contact,
    tile: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  },
  // "Inventory · inventory_2 · Amber" — amber and NOT red, and §5 says why:
  // "that matches the colour meanings used across the whole product."
  inventory: {
    icon: Package,
    tile: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    pill: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  // "Expire Date · event_busy · Red" — "the only red category".
  expiry: {
    icon: CalendarX2,
    tile: 'bg-red-500/10 text-red-700 dark:text-red-400',
    pill: 'bg-red-500/10 text-red-700 dark:text-red-400',
  },
  // "Reminder · notifications_active · Grey"
  reminder: {
    icon: Bell,
    tile: 'bg-muted text-muted-foreground',
    pill: 'bg-muted text-muted-foreground',
  },
};

/**
 * §5 field 9 — "How long ago. Written as a relative time up to a couple of
 * days, then as a day name or date. Never wraps."
 *
 * `Intl.RelativeTimeFormat` rather than a hand-rolled table, because the
 * plural rules for "منذ ٣ أيام" are not the English ones and a hand-rolled
 * table gets Arabic duals wrong.
 *
 * THE READER'S CLOCK, not the company's, and this is the same exception the
 * audit log makes: "10 min ago" is a statement about the reader's own
 * afternoon. Every business DATE in the message body — an expiry, a due date —
 * is still the company's, because it was resolved server-side on the company
 * clock before it ever became a param.
 */
function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.round((then - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const minutes = seconds / 60;
  if (Math.abs(minutes) < 60) return rtf.format(Math.round(minutes), 'minute');
  const hours = minutes / 60;
  if (Math.abs(hours) < 24) return rtf.format(Math.round(hours), 'hour');
  const days = hours / 24;
  // "up to a couple of days, then as a day name or date"
  if (Math.abs(days) < 3) return rtf.format(Math.round(days), 'day');
  return new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

const CHIP =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors';

export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // §6: "Exactly one may be chosen. All is chosen when the screen opens."
  const [category, setCategory] = useState<NotificationCategory | ''>('');
  // §18's Load more, accumulated here: pages APPEND, so the reader keeps their
  // place instead of being returned to the top of a list they scrolled.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState<NotificationRow[]>([]);
  // ERR-ADM-04-01 / ERR-ADM-04-02 — shown where the reader clicked, not as a
  // toast that vanishes before it is read.
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isPending, isError } = useNotifications({
    ...(category ? { category } : {}),
    ...(cursor ? { before: cursor } : {}),
  });
  const markAll = useMarkAllRead();
  const open = useOpenNotification();

  useEffect(() => {
    document.title = pageTitle(t('notif.title'));
  }, [t]);

  // A chip change is a NEW list, never an addition to the old one.
  useEffect(() => {
    setCursor(undefined);
    setLoaded([]);
    setNotice(null);
  }, [category]);

  useEffect(() => {
    if (!data) return;
    setLoaded((current) =>
      cursor === undefined
        ? data.notifications
        : // Guard against a double render appending the same page twice.
          [...current, ...data.notifications.filter((row) => !current.some((r) => r.id === row.id))],
    );
  }, [data, cursor]);

  const unread = data?.unread;
  const rows = loaded;

  // BR-ADM-04-07's greying: §17's ERR-ADM-04-04 row says the button IS greyed
  // out when nothing in view is unread, rather than clicking to no effect.
  const unreadInView = useMemo(() => {
    if (!unread) return 0;
    return category ? (unread.byCategory[category] ?? 0) : unread.total;
  }, [unread, category]);

  async function openRow(row: NotificationRow) {
    setNotice(null);
    const result = await open.mutateAsync(row.id);
    // Optimistic locally so the dot clears even when the destination refuses:
    // BR-ADM-04-06 marks it read in every outcome, and the server already did.
    setLoaded((current) => current.map((r) => (r.id === row.id ? { ...r, read: true } : r)));
    if (result.outcome === 'forbidden') {
      setNotice(t('notif.errForbidden'));
      return;
    }
    if (result.outcome === 'gone' || !result.linkType) {
      setNotice(t('notif.errGone'));
      return;
    }
    void navigate(pathForNotification(result.linkType, result.linkId));
  }

  return (
    // §4: "A single column, 860 pixels wide at most."
    <div className="mx-auto w-full max-w-[860px] space-y-4">
      {/* §4's header bar, wrapping onto a second line when the window is narrow. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-[9px]">
          <button
            type="button"
            onClick={() => setCategory('')}
            aria-pressed={category === ''}
            className={cn(
              CHIP,
              category === ''
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            <Inbox className="size-3.5" aria-hidden />
            {t('notif.chipAll')}
            <Badge count={unread?.total ?? 0} active={category === ''} />
          </button>
          {NOTIFICATION_CATEGORIES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setCategory(option)}
              aria-pressed={category === option}
              className={cn(
                CHIP,
                category === option
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`notif.cat.${option}`)}
              <Badge count={unread?.byCategory[option] ?? 0} active={category === option} />
            </button>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ms-auto"
          // ERR-ADM-04-04: greyed when nothing in view is unread.
          disabled={unreadInView === 0 || markAll.isPending}
          onClick={() => {
            // BR-ADM-04-07 — no confirmation. Deliberate: the act is reversible
            // in the only sense that matters (nothing is destroyed), and §6
            // says "Asks for no confirmation" outright.
            markAll.mutate(category || undefined, {
              onSuccess: () =>
                setLoaded((current) => current.map((row) => ({ ...row, read: true }))),
            });
          }}
        >
          <CheckCheck className="size-4" aria-hidden />
          {t('notif.markAll')}
        </Button>
      </div>

      {notice ? (
        <div
          role="status"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm"
        >
          {notice}
        </div>
      ) : null}

      {isError ? (
        // ERR-ADM-04-05.
        <div role="alert" className="rounded-md border border-destructive/40 px-4 py-3 text-sm">
          {t('notif.errLoad')}
        </div>
      ) : null}

      {isPending && rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">{t('shell.loading')}</div>
      ) : null}

      {!isPending && rows.length === 0 && !isError ? <EmptyState category={category} /> : null}

      <ul className="space-y-2">
        {rows.map((row) => (
          <NotificationItem
            key={row.id}
            row={row}
            locale={i18n.language}
            onOpen={() => void openRow(row)}
          />
        ))}
      </ul>

      {/* §18 — "A Load more button loading 25 at a time must be added." */}
      {data?.hasMore ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCursor(rows[rows.length - 1]?.id)}
            disabled={isPending}
          >
            {t('notif.loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * BR-ADM-04-03 — "Chip badges must count UNREAD only, and a chip with nothing
 * unread must show NO BADGE AT ALL." Not a zero: absent.
 */
function Badge({ count, active }: { count: number; active: boolean }) {
  if (count === 0) return null;
  return (
    <span
      className={cn(
        'rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
        active ? 'bg-primary-foreground/20' : 'bg-muted',
      )}
    >
      {count}
    </span>
  );
}

function NotificationItem({
  row,
  locale,
  onOpen,
}: {
  row: NotificationRow;
  locale: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const style = CATEGORY_STYLE[row.category];
  // A notification whose payload names no currency is about a company-level
  // figure (a debt ceiling, a stock valuation), which GEN-107 holds in the
  // base — read from the session, not assumed.
  const base = useBaseCurrency();
  const Icon = style.icon;

  // The body's params, with money formatted the way every other amount in the
  // product is formatted — §18: "an amount in a notification must be shown in
  // the document's own currency with its own symbol, and must not be
  // converted", which is exactly what passing its own currency code does.
  const params: Record<string, string | number> = { ...row.params };
  if (typeof row.params['amount'] === 'string') {
    params['amount'] = formatMoney(row.params['amount'], String(row.params['currency'] ?? base));
  }
  if (typeof row.params['limit'] === 'string') {
    params['limit'] = formatMoney(row.params['limit'], String(row.params['currency'] ?? base));
  }

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex w-full items-start gap-3 rounded-lg border border-border p-4 text-start transition-colors hover:bg-muted/40',
          // §4: "Unread rows are tinted with a very light wash of the brand
          // colour — 4% of it mixed into the card background."
          row.read ? 'bg-card' : 'bg-primary/4',
        )}
      >
        {/* §5 field 4 — "a 40-pixel rounded tile carrying the category's icon". */}
        <span
          className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', style.tile)}
          aria-hidden
        >
          <Icon className="size-5" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{t(`notif.title.${row.kind}`, params)}</span>
            {/* §5 field 6 — an 8px brand dot after the title, unread rows only. */}
            {row.read ? null : (
              <span
                className="size-2 rounded-full bg-primary"
                aria-label={t('notif.unread')}
                role="img"
              />
            )}
            {/* §5 field 7 — the category tag pill, in the tile's colours. */}
            <span className={cn('rounded-full px-2 py-0.5 text-[11px]', style.pill)}>
              {t(`notif.cat.${row.category}`)}
            </span>
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            {t(`notif.body.${row.kind}`, params)}
          </span>
        </span>

        {/* §5 field 9 — the time, pinned to the end, never wrapping. */}
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          {relativeTime(row.createdAt, locale)}
        </span>
      </button>
    </li>
  );
}

/**
 * §18 — "a filter that matches nothing shows a centred block with a
 * `notifications_off` icon and the words 'No notifications here'… A build
 * SHOULD word it per chip — 'No stock alerts right now' is better news than a
 * generic line."
 */
function EmptyState({ category }: { category: NotificationCategory | '' }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 py-14 text-center">
      <BellOff className="size-8 text-muted-foreground" aria-hidden />
      <p className="text-sm text-muted-foreground">
        {t(category ? `notif.empty.${category}` : 'notif.empty.all')}
      </p>
    </div>
  );
}
